import React, { useContext, useEffect, useState } from 'react';
import { Autocomplete, Box, Button, Card, Chip, CircularProgress, FormControl, FormControlLabel, FormLabel, Grid, IconButton, InputAdornment, MenuItem, Modal, Radio, RadioGroup, Select, Stack, TextField, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { Add, ArrowBack } from '@mui/icons-material';
import { useForm, Controller, FormProvider, set, useFormContext } from 'react-hook-form';
import { isMobile } from 'react-device-detect';
import Swal from 'sweetalert2';

import AddToPhotosIcon from '@mui/icons-material/AddToPhotos';
import { Helpers } from '../../../services/Helpers';
import dayjs from 'dayjs';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { CouponDataContext } from './AddEditCoupon';
import { CustomerService } from '../../Customer/Service';
import { CouponServices } from './CouponServices';
import generateAlphanumeric from '../../../utils/generateAlphanumeric';
import isExpired from '../../../utils/checkCouponExpiry';

const CouponForm = () => {
    const navigate = useNavigate();
    const { couponData } = useContext(CouponDataContext);
    const { control, watch, handleSubmit, setValue, formState: { isDirty, errors, dirtyFields }, reset } = useFormContext({
        mode: 'onSubmit',
        defaultValues: couponData,
    })
    const startDate = watch('startDate');
    const endDate = watch('expireDate');

    const [isLoadingCustomerList, setIsLoadingCustomerList] = useState(false);
    const [customerList, setCustomerList] = useState([]);

    // fetch customer list
    const fetchCustomerlist = async () => {
        setIsLoadingCustomerList(true)
        try {
            const customerListResponse = await CustomerService.unsortedCustomerList();
            if (customerListResponse.success === 1) {
                setCustomerList(customerListResponse.data)
            }
        } catch (error) {

        } finally {
            setIsLoadingCustomerList(false);
        }
    }
    useEffect(() => {
        fetchCustomerlist();
    }, [])
    // Helper function to format customer name
    const getFullName = (profile) => {
        if (!profile) return '';
        return [profile.firstName, profile.middleName, profile.lastName]
            .filter(Boolean)
            .join(' ');
    };
    const couponType = watch('couponType');
    useEffect(()=>{
        if(couponType == 'fixed'){
            setValue('couponValue', 100,{shouldValidate:true});
        }else if(couponType == 'percentage'){
            setValue('couponValue', couponData.couponValue || 0,{shouldValidate:true});
        }
    },[couponType])

    const generateCouponCode = async () => {
        const code  = await generateAlphanumeric(12, true, false, true, 'CUDEL', '');
        setValue('couponCode', code, { shouldValidate: true });
    }
    return (
        <>
            
            <Grid container spacing={2}>
                <Grid item xs={12} sm={5.5}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Coupon Title <Box component={'span'} sx={{ color: '#d32f2f' }}>*</Box></Typography>
                    <Controller
                        name="title"
                        control={control}
                        rules={{
                            required: 'Enter Title',
                        }}
                        render={({ field, fieldState: { error } }) =>
                            <TextField
                                {...field}
                                fullWidth
                                size='small'
                                error={!!error}
                                InputProps={{
                                    style: { fontSize: '0.75rem' }
                                }}
                            />
                        }
                    />
                </Grid>
                <Grid item xs={1} sx={{ display: { xs: 'none', md: 'block' } }} />
                <Grid item xs={12} sm={5.5}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Select Coupon Type <Box component={'span'} sx={{ color: '#d32f2f' }}>*</Box></Typography>
                    <Controller
                        name="couponType"
                        control={control}
                        rules={{
                            required: 'Select Coupon Type',
                        }}
                        render={({ field, fieldState: { error } }) => (
                            <FormControl sx={{ minWidth: "100%" }} size="small">
                                <Select
                                    {...field}
                                    error={!!error}
                                    sx={{
                                        fontSize: '0.75rem',
                                    }}
                                >
                                    <MenuItem value={'fixed'} sx={{ textTransform: 'capitalize', fontSize: '0.75rem' }}>Fixed</MenuItem>
                                    <MenuItem value={'percentage'} sx={{ textTransform: 'capitalize', fontSize: '0.75rem' }}>Percentage</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                    />
                </Grid>


                <Grid item xs={12} sm={5.5}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center',justifyContent:'space-between' }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Coupon Code <Box component={'span'} sx={{ color: '#d32f2f' }}>*</Box></Typography>
                    <Button size='small' color='success' sx={{textTransform:'none'}} onClick={generateCouponCode}>Generate Code</Button>
                    </Box>
                        <Controller
                            name="couponCode"
                            control={control}
                            rules={{
                                required: 'Enter Coupon Code',
                                // alphanumeric minimum 3 and maximum 12 characters
                                pattern: {
                                    value: /^[a-zA-Z0-9]{3,12}$/,
                                    message: 'Coupon Code must be alphanumeric with minimum 3 maximum 12 characters',
                                },
                            }}
                            render={({ field, fieldState: { error } }) =>
                                <TextField
                                    {...field}
                                    fullWidth
                                    size='small'
                                    error={!!error}
                                    placeholder='Enter Custom Coupon Code'
                                    InputProps={{
                                        style: { fontSize: '0.75rem' }
                                    }}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        //uppercase the value and max length 12
                                        const formattedValue = value.toUpperCase().slice(0, 12);
                                        field.onChange(formattedValue);
                                    }}
                                />
                            }
                        />
                    
                </Grid>

                <Grid item xs={1} sx={{ display: { xs: 'none', md: 'block' } }} />


                <Grid item xs={12} sm={5.5}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600,color:couponType=='fixed'?'lightGray':'black' }}>Coupon Value(%) <Box component={'span'} sx={{ color:couponType=='fixed'?'lightGray':'#d32f2f' }}>*</Box></Typography>
                    <Controller
                        name="couponValue"
                        control={control}
                        rules={{
                            required: 'Enter Coupon Code',
                            // should be greater than 0 and less than 100
                            validate: (value) => {
                                if (couponType == 'percentage') {
                                    if (value <= 0 || value >= 100) {
                                        return 'Coupon Value should be between 1 and 100';
                                    }
                                } 
                                
                                return true;
                            },
                        }}
                        render={({ field, fieldState: { error } }) =>
                            <TextField
                                {...field}
                                type='number'
                                disabled={couponType == 'fixed'}
                                fullWidth
                                size='small'
                                error={!!error}
                                InputProps={{
                                    style: { fontSize: '0.75rem' },
                                    onWheel: (e) => e.target.blur(),  // Prevent scroll wheel
                                    onKeyDown: (e) => {
                                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                        e.preventDefault();
                                    }
                                    }
                                }}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    // allow decimal value  and remove leading zeros
                                    const formattedValue = value.replace(/^0+/, '');
                                    const maxValue = formattedValue >= 100 ? 99 : formattedValue;
                                    field.onChange(maxValue);
                                }}
                            />
                        }
                    />
                </Grid>
                <Grid item xs={12} sm={5.5}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Min. Booking Amt. <Box component={'span'} sx={{ color: '#d32f2f' }}>*</Box></Typography>
                    <Controller
                        name="minimumBookingAmt"
                        control={control}
                        rules={{
                            required: 'Enter Min. Booking Amt.',
                            validate: (value) => {
                                    if (value <= 0) {
                                        return 'Min. Booking Amt. should be greater than 0';
                                    }
                                
                                
                                return true;
                            },
                        }}
                        render={({ field, fieldState: { error } }) =>
                            <TextField
                                {...field}
                                fullWidth
                                type='number'
                                size='small'
                                error={!!error}
                                InputProps={{
                                    style: { fontSize: '0.75rem' },
                                    onWheel: (e) => e.target.blur(),  // Prevent scroll wheel
                                    onKeyDown: (e) => {
                                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                        e.preventDefault();
                                    }
                                    },
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Typography sx={{fontSize: '0.65rem'}}>₹</Typography>
                                        </InputAdornment>
                                    )
                                }}
                                
                                onChange={(e) => {
                                    const value = e.target.value;
                                    // allow decimal value  and remove leading zeros
                                    const formattedValue = value.replace(/^0+/, '');
                                    // const maxValue = formattedValue >= 100 ? 99 : formattedValue;
                                    field.onChange(formattedValue);
                                }}
                            />
                        }
                    />
                </Grid>
                <Grid item xs={1} sx={{ display: { xs: 'none', md: 'block' } }} />
                <Grid item xs={12} sm={5.5}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Max. Disount Amt. <Box component={'span'} sx={{ color: '#d32f2f' }}>*</Box></Typography>
                    <Controller
                        name="maximumDiscount"
                        control={control}
                        rules={{
                            required: 'Enter Max. Disount Amt.',
                            validate: (value) => {
                                if (value <= 0) {
                                    return 'Max. Disount Amt. should be greater than 0';
                                }
                            
                            
                            return true;
                        },
                        }}
                        render={({ field, fieldState: { error } }) =>
                            <TextField
                                {...field}
                                fullWidth
                                type='number'
                                size='small'
                                error={!!error}
                                InputProps={{
                                    style: { fontSize: '0.75rem' },
                                    onWheel: (e) => e.target.blur(),  // Prevent scroll wheel
                                    onKeyDown: (e) => {
                                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                        e.preventDefault();
                                    }
                                    },
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Typography sx={{fontSize: '0.65rem'}}>₹</Typography>
                                        </InputAdornment>
                                    )
                                }}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    // allow decimal value  and remove leading zeros
                                    const formattedValue = value.replace(/^0+/, '');
                                    // const maxValue = formattedValue >= 100 ? 99 : formattedValue;
                                    field.onChange(formattedValue);
                                }}
                            />
                        }
                    />
                </Grid>

                <Grid item xs={12} sm={5.5}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Start Date <Box component={'span'} sx={{ color: '#d32f2f' }}>*</Box></Typography>
                    <Controller
                        control={control}
                        name="startDate"
                        // defaultValue={dayjs().format('YYYY-MM-DD')}
                        rules={{
                            required: 'Start Date is required',
                            validate: (value) => {
                                if (!value) return "This field is required";
                                const date = dayjs(value);
                                const today = dayjs().startOf('day');

                                if (!date.isValid()) return "Invalid date";
                                if (couponData.id>0 && !isExpired(couponData?.endDate)){
                                    return true;
                                }else if (date.isBefore(today)) return "Start date must be in the future";

                                return true;
                            }
                        }}
                        render={({ field: { onChange, value, ...restField }, fieldState: { error } }) => (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DesktopDatePicker
                                    {...restField}
                                    sx={{
                                        width: '100%',
                                        '& .MuiInputBase-root': {
                                            fontSize: '0.75rem'
                                        }
                                    }}
                                    value={value ? dayjs(value) : null}
                                    onChange={(newValue) => {
                                        onChange(newValue ? newValue.format("YYYY-MM-DD") : null);
                                    }}
                                    format="DD-MM-YYYY"
                                    minDate={dayjs()}
                                    slotProps={{
                                        textField: {
                                            error: !!error,
                                            // helperText: error?.message,
                                            fullWidth: true,
                                            size: 'small',
                                            inputProps: {
                                                style: { fontSize: '.75rem' }
                                            }
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                        )}
                    />
                </Grid>
                <Grid item xs={1} sx={{ display: { xs: 'none', md: 'block' } }} />
                <Grid item xs={12} sm={5.5}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600,color:!startDate?'lightGray':'black' }}>End Date <Box component={'span'} sx={{ color: !startDate?'lightGray':'#d32f2f' }}>*</Box></Typography>
                    <Controller
                        control={control}
                        name="endDate"
                        // defaultValue={dayjs().add(1, 'day').format('YYYY-MM-DD')}
                        rules={{
                            required: 'End Date is required',
                            validate: (value) => {
                                if (!value) return "This field is required";
                                const date = dayjs(value);
                                const startDateValue = watch('startDate');
                                const startDate = dayjs(startDateValue);

                                if (!date.isValid()) return "Invalid date";
                                if (date.isBefore(startDate)) return "End date must be after start date";
                                // if(date.isSame(startDate)) return "End date must be after start date";

                                return true;
                            }
                        }}
                        render={({ field: { onChange, value, ...restField }, fieldState: { error } }) => (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DesktopDatePicker
                                    {...restField}
                                    disabled={!startDate}
                                    sx={{
                                        width: '100%',
                                        '& .MuiInputBase-root': {
                                            fontSize: '0.75rem'
                                        }
                                    }}
                                    value={value ? dayjs(value) : null}
                                    onChange={(newValue) => {
                                        onChange(newValue ? newValue.format("YYYY-MM-DD") : null);
                                    }}
                                    format="DD-MM-YYYY"
                                    //after today and after startdate
                                    // minDate={watch('startDate') ? dayjs(watch('startDate')).add(1, 'day') : dayjs().add(1, 'day')}
                                    minDate={watch('startDate') ? dayjs(watch('startDate')) : dayjs()}
                                    slotProps={{
                                        textField: {
                                            error: !!error,
                                            // helperText: error?.message,
                                            fullWidth: true,
                                            size: 'small',
                                            inputProps: {
                                                style: { fontSize: '.75rem' }
                                            }
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                        )}
                    />
                </Grid>

                <Grid item xs={12} sm={5.5}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Select Customers</Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Controller
                            name="selectedCustomers"
                            control={control}
                            defaultValue={[]}  // Keep default value as null when no selection
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <Autocomplete
                                    fullWidth
                                    multiple // Enable multiple selection
                                    options={customerList || []}
                                    getOptionLabel={(option) => {
                                        if (!option) return '';
                                        const profile = option.profile || {};
                                        const fullName = getFullName(profile);
                                        return fullName ? `${fullName} (${option.mobileNumber || ''})` : '';
                                    }}
                                    value={value || []}
                                    onChange={(event, newValues) => {
                                        if (newValues && newValues.length > 0) {
                                            // Map the selected values to only include the IDs
                                            const selectedIds = newValues.map(item => ({ id: item.id }));
                                            onChange(selectedIds);
                                        } else {
                                            // If no customers selected, set value to null
                                            onChange([]);
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            size='small'
                                            placeholder='Select customers'
                                            variant="outlined"
                                            error={!!error}
                                            InputProps={{
                                                ...params.InputProps,
                                                style: { fontSize: '0.75rem', color: '#000' },
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => {
                                        const profile = option.profile || {};
                                        const fullName = getFullName(profile);
                                        return (
                                            <MenuItem
                                                {...props}
                                                style={{
                                                    fontSize: '0.8rem',
                                                    padding: '8px 16px',
                                                    borderBottom: '1px solid #f0f0f0'
                                                }}
                                            >
                                                <Box style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <Box style={{ fontWeight: '600', color: '#3e3e3e' }}>
                                                        {fullName}
                                                        {option.isTester &&
                                                            <span style={{
                                                                marginLeft: '8px',
                                                                background: '#ffecb3',
                                                                padding: '1px 4px',
                                                                borderRadius: '4px',
                                                                fontSize: '0.65rem'
                                                            }}>
                                                                Tester
                                                            </span>
                                                        }
                                                        <span style={{ marginLeft: '8px', fontSize: '0.7rem', color: '#666' }}>
                                                            ({option.mobileNumber})
                                                        </span>
                                                    </Box>
                                                </Box>
                                            </MenuItem>
                                        );
                                    }}
                                    isOptionEqualToValue={(option, value) => {
                                        if (!option || !value) return false;
                                        return option.id === value.id;
                                    }}
                                    noOptionsText={<span style={{ fontSize: '0.75rem' }}>No customers found</span>}
                                    ListboxProps={{
                                        style: {
                                            maxHeight: '250px',
                                            fontSize: '0.8rem'
                                        }
                                    }}
                                    PaperProps={{
                                        style: {
                                            marginTop: '4px',
                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                        }
                                    }}
                                    // Add chip styling for selected items
                                    renderTags={(tagValue, getTagProps) =>
                                        tagValue.map((option, index) => {
                                            const profile = customerList.find(item => item.id === option.id)?.profile || {};
                                            const fullName = getFullName(profile);
                                            return (
                                                <Chip
                                                    label={fullName}
                                                    size="small"
                                                    {...getTagProps({ index })}
                                                    style={{
                                                        fontSize: '0.7rem',
                                                        height: '24px',
                                                        marginRight: '4px'
                                                    }}
                                                />
                                            );
                                        })
                                    }
                                />                                        
                            )}
                        />
                        {isLoadingCustomerList && <CircularProgress />}
                    </Box>
                </Grid><Grid item xs={1} sx={{ display: { xs: 'none', md: 'block' } }} />
                
                <Grid item xs={12} sm={5.5}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>limit Per User <Box component={'span'} sx={{ color: '#d32f2f' }}>*</Box></Typography>
                    <Controller
                        control={control}
                        name="limitPerUser"
                        rules={{
                            required: 'Enter limit for coupon use',
                            validate: (value) => {
                                if (value <= 0) {
                                    return 'Limit should be greater than 0';
                                }
                                
                                return true;
                            },
                        }}
                        render={({ field, fieldState: { error } }) => (
                            <TextField
                                {...field}
                                fullWidth
                                type='number'
                                size='small'
                                error={!!error}
                                onChange={
                                    (e) => {
                                        const value = e.target.value;
                                        // allow integer value  and remove leading zeros and minimum 1 
                                        const formattedValue = value.replace(/^0+/, '');
                                        const maxValue = formattedValue >= 0 ? formattedValue : 1;
                                        field.onChange(maxValue);
                                        
                                    }   
                                }
                                InputProps={{
                                    style: { fontSize: '0.65rem' }
                                }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} sm={5.5}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Description <Box component={'span'} sx={{ color: '#d32f2f' }}>*</Box></Typography>
                    <Controller
                        control={control}
                        name="description"
                        rules={{
                            required: 'Description is required',
                        }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                size='small'
                                error={!!error}
                                value={value}
                                onChange={onChange}
                                InputProps={{
                                    style: { fontSize: '0.65rem' }
                                }}
                            />
                        )}
                    />
                </Grid>
                
            </Grid>
        </>
    );
}

export default CouponForm;

const formatValue = (value) => {
    if (typeof value !== 'string') {
        return value === 0 ? '0' : value?.toString() || '';
    }
    const formatted = value.replace(/^0+(?=\d)/, '').replace(/^\./, '0.');
    return formatted === '' ? '' : formatted;
};